import React, { useState, useCallback } from 'react';
import MigrationDashboard from './MigrationDashboard';
import MigrationWizard from './MigrationWizard';
import LiveProgressPanel from './LiveProgressPanel';
import JobDetailPanel from './JobDetailPanel';
import SidePanel from '../../ui/SidePanel';
import useMigrationJobs from './hooks/useMigrationJobs';

export default function EmailMigration({ showNotification }) {
  const [showWizard, setShowWizard] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null); // for live progress
  const [detailJobId, setDetailJobId] = useState(null); // for past job view
  const { jobs, loading, error, refresh } = useMigrationJobs();

  const handleMigrationStarted = useCallback((migrationId) => {
    setShowWizard(false);
    setActiveJobId(migrationId);
    refresh();
    showNotification?.('Migration started successfully', 'success');
  }, [refresh, showNotification]);

  const handleViewJob = useCallback((jobId) => {
    // Check if job is running
    const job = jobs.find(j => j.jobId === jobId || j._id === jobId);
    if (job && job.status === 'running') {
      setActiveJobId(job.jobId || jobId);
    } else {
      setDetailJobId(jobId);
    }
  }, [jobs]);

  const handleRetry = useCallback(async (jobId) => {
    const job = jobs.find(j => j.jobId === jobId || j._id === jobId);
    if (job) {
      setActiveJobId(job.jobId || jobId);
    }
    refresh();
  }, [jobs, refresh]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Email Migration</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Migrate emails from external providers to your mail server
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Migration
        </button>
      </div>

      {/* Dashboard */}
      <MigrationDashboard
        jobs={jobs}
        loading={loading}
        error={error}
        onRefresh={refresh}
        onViewJob={handleViewJob}
        onRetry={handleRetry}
        onNewMigration={() => setShowWizard(true)}
        showNotification={showNotification}
      />

      {/* Wizard Panel */}
      <SidePanel
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        title="New Email Migration"
        size="3xl"
      >
        <MigrationWizard
          onClose={() => setShowWizard(false)}
          onStarted={handleMigrationStarted}
          showNotification={showNotification}
        />
      </SidePanel>

      {/* Live Progress Panel */}
      <SidePanel
        isOpen={!!activeJobId}
        onClose={() => { setActiveJobId(null); refresh(); }}
        title="Migration Progress"
        size="2xl"
      >
        {activeJobId && (
          <LiveProgressPanel
            migrationId={activeJobId}
            onClose={() => { setActiveJobId(null); refresh(); }}
            showNotification={showNotification}
          />
        )}
      </SidePanel>

      {/* Job Detail Panel */}
      <SidePanel
        isOpen={!!detailJobId}
        onClose={() => setDetailJobId(null)}
        title="Migration Details"
        size="2xl"
      >
        {detailJobId && (
          <JobDetailPanel
            jobId={detailJobId}
            onClose={() => setDetailJobId(null)}
            onRetry={(id) => { setDetailJobId(null); handleRetry(id); }}
            showNotification={showNotification}
          />
        )}
      </SidePanel>
    </div>
  );
}
